import { TWidget } from 'widgets/Widget';

/**
 * @param Widget Base widget for extending
 * @returns GroupedScrollSlider class
 */
function GroupedScrollSliderClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory pagedesigner
     * @class GroupedScrollSlider
     * @augments Widget
     * @classdesc Represents Grouped Scroll Slider component with features:
     * 1. Switch visible carousel group via tab click
     * 2. Support keyboard navigation (ArrowLeft/ArrowRight/Home/End/Enter/Space)
     * 3. Keep ARIA state in sync for tabs and tab panels
     * 4. Trigger resize recalculation after panel switch (for inner scrollCarousel layout)
     *
     * @property {string} data-widget - Widget name `groupedScrollSlider`
     * @property {string} data-event-click - Event listener method `onTabClick` on tab buttons
     * @property {string} data-event-keydown - Event listener method `onTabKeydown` on tab buttons
     *
     * @example <caption>Example of Grouped Scroll Slider widget usage</caption>
     * <div id="${pdict.ID}" class="b-grouped-scroll-slider" data-widget="groupedScrollSlider">
     *     <h3 class="b-grouped-scroll-slider-title">${pdict.title}</h3>
     *     <div class="b-grouped-scroll-slider-top">
     *         <div class="b-grouped-scroll-slider-tabs" role="tablist" aria-label="${pdict.title}">
     *             <button
     *                 id="${tab.id}"
     *                 class="b-grouped-scroll-slider-tab m-active"
     *                 role="tab"
     *                 aria-selected="true"
     *                 aria-controls="${tab.panelId}"
     *                 tabindex="0"
     *                 data-event-click="onTabClick"
     *                 data-event-keydown="onTabKeydown">
     *                 <span class="b-grouped-scroll-slider-tab-icon">
     *                     <img src="${tab.iconImage.srcLarge}" alt="${tab.iconAlt}" />
     *                 </span>
     *                 <span class="b-grouped-scroll-slider-tab-title">${tab.title}</span>
     *             </button>
     *         </div>
     *     </div>
     *     <div class="b-grouped-scroll-slider-panels">
     *         <section
     *             id="${tab.panelId}"
     *             class="b-grouped-scroll-slider-panel"
     *             role="tabpanel"
     *             aria-labelledby="${tab.id}">
     *             <div class="b-scroll-carousel" data-widget="scrollCarousel">
     *                 <!-- carousel content -->
     *             </div>
     *         </section>
     *     </div>
     * </div>
     */
    class GroupedScrollSlider extends Widget {
        tabs: Array<HTMLButtonElement> = [];

        panels: Array<HTMLElement> = [];

        activeIndex = 0;

        init() {
            super.init();

            const root = this.ref('self').get();

            if (!root) {
                return;
            }

            this.tabs = Array.from(root.querySelectorAll('.b-grouped-scroll-slider-tab'));
            this.panels = Array.from(root.querySelectorAll('.b-grouped-scroll-slider-panel'));

            if (!this.tabs.length || !this.panels.length) {
                return;
            }

            this.activate(0, false);
        }

        private resolveSourceElement(source: unknown, event?: Event): HTMLElement | null {
            if (source instanceof HTMLElement) {
                return source;
            }

            if (
                source
                && typeof source === 'object'
                && 'get' in source
                && typeof (source as { get: () => unknown }).get === 'function'
            ) {
                const fromRef = (source as { get: () => unknown }).get();

                if (fromRef instanceof HTMLElement) {
                    return fromRef;
                }
            }

            if (event?.currentTarget instanceof HTMLElement) {
                return event.currentTarget;
            }

            if (event?.target instanceof HTMLElement) {
                return event.target;
            }

            return null;
        }

        private resolveTabElement(source: unknown, event?: Event): HTMLButtonElement | null {
            const el = this.resolveSourceElement(source, event);

            if (!el) {
                return null;
            }

            if (el.classList.contains('b-grouped-scroll-slider-tab')) {
                return el as HTMLButtonElement;
            }

            return el.closest('.b-grouped-scroll-slider-tab');
        }

        private activate(index: number, moveFocus = true) {
            const safeIndex = Math.max(0, Math.min(index, this.tabs.length - 1));

            this.activeIndex = safeIndex;

            this.tabs.forEach((tab, tabIndex) => {
                const isActive = tabIndex === safeIndex;

                tab.classList.toggle('m-active', isActive);
                tab.setAttribute('aria-selected', String(isActive));
                tab.setAttribute('tabindex', isActive ? '0' : '-1');

                if (isActive && moveFocus) {
                    tab.focus();
                }
            });

            this.panels.forEach((panel, panelIndex) => {
                if (panelIndex === safeIndex) {
                    panel.removeAttribute('hidden');
                } else {
                    panel.setAttribute('hidden', 'hidden');
                }
            });

            window.requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'));
            });
        }

        private getTabIndexByElement(source: unknown, event?: Event): number {
            const resolvedTab = this.resolveTabElement(source, event);

            if (!resolvedTab) {
                return -1;
            }

            return this.tabs.findIndex(item => item === resolvedTab);
        }

        onTabClick(source: unknown, event: Event) {
            const clickedIndex = this.getTabIndexByElement(source, event);

            if (clickedIndex >= 0) {
                this.activate(clickedIndex, false);
            }
        }

        onTabKeydown(source: unknown, event: KeyboardEvent) {
            const currentIndex = this.getTabIndexByElement(source, event);
            let nextIndex = currentIndex;

            if (currentIndex < 0) {
                return;
            }

            switch (event.key) {
                case 'ArrowRight':
                case 'Right':
                    nextIndex = (currentIndex + 1) % this.tabs.length;
                    break;
                case 'ArrowLeft':
                case 'Left':
                    nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'End':
                    nextIndex = this.tabs.length - 1;
                    break;
                case 'Enter':
                case ' ':
                case 'Spacebar':
                    this.activate(currentIndex, false);
                    event.preventDefault();

                    return;
                default:
                    return;
            }

            event.preventDefault();
            this.activate(nextIndex);
        }
    }

    return GroupedScrollSlider;
}

export type TGroupedScrollSlider = ReturnType<typeof GroupedScrollSliderClassCreator>;

export default GroupedScrollSliderClassCreator;
